import { Stack, styled, Box, useTheme, useMediaQuery, Link } from '@mui/material';

import Grid from '@mui/material/Unstable_Grid2';
import useTranslation from 'canvas_states/translation/useTranslation';
import Image from '../../atoms/Image';
import plusgradeAssets from '../../configs/plusgradeAssets';
import MaxWidthContainer from '../../atoms/MaxWidthContainer/MaxWidthContainer';
import { anyScreen, isMobileScreen, isTabletScreen } from '../../utils/screenSizes/screenSizes';
import Typography from '../../atoms/Typography';

/**
 * Removing this Box + padding causes playwright e2e tests to fail in
 * Mobile Safari due to z-index of the OneTrust cookie banner somehow
 * conflicting with the box-sizing behavior of the MUI `Container`
 */
const StyledFooter = styled(Box)`
  padding: 0.75rem;
`;
/**
 * to offset the padding of the above `StyledFooter`,
 * this reduces the inline padding from 1.5rem
 */
const StyledMaxWidthContainer = styled(MaxWidthContainer)(
  ({ theme }) => `

  ${theme.breakpoints.up('sm')} {
    padding-inline: 0.75rem; 
  }
`,
);

const StyledLink = styled(Link)(
  ({ theme }) => `
  display: inline-block;
  color: ${theme.palette.text.primary};
  text-decoration-color: ${theme.palette.text.primary};
  white-space: nowrap;
`,
);

const PlugradeLogo = styled(Image)`
  img {
    width: 6.5rem;
    max-width: 40vw;
  }
`;

export const dataTestId = {};

export const makeDataTestId = (dataTestId = 'footer') => {
  return {
    footer: dataTestId,
    faqUrl: `${dataTestId}.faqUrl`,
    termsAndConditionsUrl: `${dataTestId}.termsAndConditionsUrl`,
    termsAndConditionsExtraSeatUrl: `${dataTestId}.termsAndConditionsExtraSeatUrl`,
    cookiesUrl: `${dataTestId}.cookiesUrl`,
    logo: `${dataTestId}.logo`,
  };
};

export interface FooterProps {
  partnerName?: string;
  faqUrl?: string;
  termsAndConditionsUrl?: string;
  termsAndConditionsExtraSeatUrl?: string;
  hasDynamicTermsAndConditions: boolean;
  onTermsClick?: () => void;
  onPrivacyClick?: () => void;
  onLogoDoubleClick?: () => void;
}

const allDataTestId = makeDataTestId();

const Footer: React.FC<FooterProps> = ({
  partnerName,
  faqUrl,
  termsAndConditionsUrl,
  termsAndConditionsExtraSeatUrl,
  hasDynamicTermsAndConditions,
  onTermsClick,
  onPrivacyClick,
  onLogoDoubleClick = () => {},
}) => {
  const { t } = useTranslation().actions;
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(anyScreen(isMobileScreen(theme), isTabletScreen(theme)));

  const poweredBy = (
    <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" gap={{ xs: 0, md: 1 }}>
      <Typography variant="caption2" color="text.secondary">
        {t('footer.powered-by')}
      </Typography>
      <PlugradeLogo
        dataTestId={allDataTestId.logo}
        src={plusgradeAssets.logo}
        alt="Plusgrade logo"
        onDoubleClick={onLogoDoubleClick}
      />
    </Stack>
  );

  return (
    <StyledFooter>
      <StyledMaxWidthContainer>
        <Box py={10} data-testid={allDataTestId.footer}>
          <Grid container spacing={2}>
            <Grid xs={12} md={12} container justifyContent={{ xs: 'center', md: 'space-between' }}>
              <Grid>
                <Typography variant="body1" color="text.primary">
                  {partnerName} © {new Date().getFullYear()}
                </Typography>{' '}
              </Grid>
              <Grid sx={{ display: { xs: 'none', md: 'block' } }}>{poweredBy}</Grid>
            </Grid>
            <Grid xs={12} container justifyContent={{ xs: 'center', md: 'unset' }}>
              <Typography color="text.primary" variant="body1" component="div" textAlign="center">
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  rowGap={0.5}
                  px={1}
                  divider={
                    isMediumScreen ? null : (
                      <Box px={1} component="span">
                        <Typography variant="body3">•</Typography>
                      </Box>
                    )
                  }
                  component="div"
                >
                  {faqUrl && (
                    <StyledLink href={faqUrl} data-testid={allDataTestId.faqUrl} target="_blank">
                      FAQ
                    </StyledLink>
                  )}
                  <StyledLink
                    href={termsAndConditionsUrl}
                    data-testid={allDataTestId.termsAndConditionsUrl}
                    target="_blank"
                    onClick={() => onTermsClick?.()}
                  >
                    {t('payment.form.terms-and-conditions-upgrade')}
                  </StyledLink>
                  {hasDynamicTermsAndConditions && (
                    <StyledLink
                      href={termsAndConditionsExtraSeatUrl}
                      data-testid={allDataTestId.termsAndConditionsExtraSeatUrl}
                      target="_blank"
                      onClick={() => onTermsClick?.()}
                    >
                      {t('payment.form.terms-and-conditions-seating')}
                    </StyledLink>
                  )}

                  {/** className "ot-sdk-show-settings" so OneTrust can modify the content */}
                  <StyledLink
                    className="ot-sdk-show-settings"
                    data-testid={allDataTestId.cookiesUrl}
                    onClick={(event) => {
                      event.preventDefault();
                      onPrivacyClick?.();
                      window?.OneTrust?.LoadBanner();
                    }}
                  >
                    {t('footer.cookies')}
                  </StyledLink>
                </Stack>
              </Typography>
            </Grid>
            <Grid mt={0.5} xs={12} container justifyContent="center" sx={{ display: { xs: 'flex', md: 'none' } }}>
              {poweredBy}
            </Grid>
          </Grid>
        </Box>
      </StyledMaxWidthContainer>
    </StyledFooter>
  );
};

export default Footer;
